import { useEffect, useRef, useState } from "react";
import style from "./InputSelect.module.scss";
import cn from "classnames";
import { RiLoader5Fill } from "react-icons/ri";

export interface ValueInputSelect {
  value: string;
  label: string;
}

export function InputSelect({
  listValues,
  listValueActions,
  currentValue,
  onChange,
  onBlur,
  customStyle,
  label,
  name,
  error,
  readOnly = true,
  enterAction,
  loader
}: {
  listValues: ValueInputSelect[];
  listValueActions?: (itemData: ValueInputSelect) => JSX.Element,
  currentValue: ValueInputSelect;
  onChange: (value: ValueInputSelect) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void
  customStyle?: {
    container?: React.HTMLAttributes<HTMLElement>["className"];
    listValues?: React.HTMLAttributes<HTMLElement>["className"];
    value?: React.HTMLAttributes<HTMLElement>["className"];
  };
  label?: {
    text?: string,
    bg?: string
  } | string;
  name?: string,
  error?: string,
  readOnly?: boolean,
  enterAction?: (value: string) => void,
  loader?: boolean
}) {
  const [localValue, setLocalValue] = useState<string>("");

  const refContainer = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    function handleClick(e: any) {
      if (!refContainer.current?.contains(e.target)) {
        setIsOpen(false);
      }
    }

    document.documentElement.addEventListener("click", handleClick);

    return () => {
      document.documentElement.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    setLocalValue(currentValue.label)
  }, [currentValue])

  return (
    <div ref={refContainer} className={cn(style.container, customStyle?.container, isOpen && style.open)}>
      <div>
        <label
          className={style.currentValue}
          onClick={() => {
            setIsOpen((state) => true);
          }}
        >
          <input
            type={"text"}
            autoComplete="off"
            className={style.input}
            value={localValue}
            readOnly={readOnly}
            onBlur={onBlur}
            name={name}
            onChange={(e) => {
              setLocalValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                enterAction?.(localValue)
              }
            }}
          />
          <div className={cn(style.label, (currentValue.label.length || isOpen) && style.label_focus)}>
            <p className={cn("w-fit px-[2px]", ((typeof label === "object") && label?.bg) ? `bg-[${label.bg}]` : "bg-white")}>
              {typeof label === "string" ? label : label?.text}
            </p>
          </div>
          <div className={style.actions}>
            {loader ? <RiLoader5Fill className={"animate-spin"} /> : null}
            <OpenListButton isOpenList={Boolean(isOpen)} />
          </div>
        </label>
        {
          error
            ? <p className={style.error}>{error}</p>
            : null
        }
      </div>
      <div className={cn(style.listValues, customStyle?.listValues)}>
        {listValues.map(({ value, label }) => (
          <div key={value} className="flex justify-between items-center gap-[5px]">
            <div
              className={cn(
                style.value,
                value === currentValue.value && style.value_active,
                customStyle?.value,
              )}
              onClick={() => {
                if (value !== currentValue.value) {
                  onChange({ value, label });
                }
                setIsOpen(false);
              }}
            >
              {label}
            </div>
            {listValueActions?.({ value, label })}
          </div>
        ))}
      </div>
    </div>
  );
}

function OpenListButton({ isOpenList }: { isOpenList: boolean }) {
  return (
    <div className={style.openListButton}>
      <svg
        className={cn(isOpenList && "rotate-[180deg]")}
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M5.50503 9.49497C5.77839 9.76834 6.22161 9.76834 6.49497 9.49497L10.9497 5.0402C11.2231 4.76683 11.2231 4.32362 10.9497 4.05025C10.6764 3.77689 10.2332 3.77689 9.9598 4.05025L6 8.01005L2.0402 4.05025C1.76684 3.77689 1.32362 3.77689 1.05025 4.05025C0.776885 4.32362 0.776885 4.76684 1.05025 5.0402L5.50503 9.49497ZM5.3 8L5.3 9L6.7 9L6.7 8L5.3 8Z"
          fill="#A2AFD0"
        />
      </svg>
    </div>
  );
}
